import React, { useState, useEffect } from 'react';
import {
  Plus,
  Eye,
  Edit2,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Copy,
  ExternalLink,
  TrendingUp,
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
// Remove toast import - will use alerts instead
import { AdsService, Ad } from '../services/ads';
import { AdFormModal } from './AdFormModal';
import { AdAnalyticsModal } from './AdAnalyticsModal';

export const AdsList: React.FC = () => {
  const [ads, setAds] = useState<Ad[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAd, setSelectedAd] = useState<Ad | null>(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isAnalyticsModalOpen, setIsAnalyticsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    loadAds();
  }, []);

  const loadAds = async () => {
    try {
      setLoading(true);
      const adsData = await AdsService.getAds();
      setAds(adsData);
    } catch (error) {
      console.error('Failed to load ads:', error);
      setErrorMessage('Failed to load ads');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAd = () => {
    setSelectedAd(null);
    setIsEditMode(false);
    setIsFormModalOpen(true);
  };

  const handleEditAd = (ad: Ad) => {
    setSelectedAd(ad);
    setIsEditMode(true);
    setIsFormModalOpen(true);
  };

  const handleViewAnalytics = (ad: Ad) => {
    setSelectedAd(ad);
    setIsAnalyticsModalOpen(true);
  };

  const handleDeleteAd = async (ad: Ad) => {
    if (!window.confirm(`Are you sure you want to delete "${ad.name}"?`)) {
      return;
    }

    try {
      await AdsService.deleteAd(ad.id);
      setSuccessMessage('Ad deleted successfully');
      loadAds();
    } catch (error) {
      console.error('Failed to delete ad:', error);
      setErrorMessage('Failed to delete ad');
    }
  };

  const handleToggleTracking = async (ad: Ad) => {
    try {
      await AdsService.toggleTracking(ad.id);
      setSuccessMessage(
        `Tracking ${ad.userTrackingEnabled ? 'disabled' : 'enabled'} for "${ad.name}"`
      );
      loadAds();
    } catch (error) {
      console.error('Failed to toggle tracking:', error);
      setErrorMessage('Failed to toggle tracking');
    }
  };

  const handleCopyTrackingUrl = async (ad: Ad) => {
    const success = await AdsService.copyToClipboard(ad.trackingUrl);
    if (success) {
      setSuccessMessage('Tracking URL copied to clipboard');
    } else {
      setErrorMessage('Failed to copy tracking URL');
    }
  };

  const handleFormSuccess = () => {
    setIsFormModalOpen(false);
    setSelectedAd(null);
    loadAds();
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center p-8'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Alert Messages */}
      {errorMessage && (
        <div className='alert-enhanced alert-error'>
          <span className='text-red-500'>⚠️</span>
          <span>{errorMessage}</span>
          <button
            onClick={() => setErrorMessage(null)}
            className='ml-auto text-red-500 hover:text-red-700'
          >
            ×
          </button>
        </div>
      )}
      {successMessage && (
        <div className='alert-enhanced alert-success'>
          <span className='text-green-500'>✅</span>
          <span>{successMessage}</span>
          <button
            onClick={() => setSuccessMessage(null)}
            className='ml-auto text-green-500 hover:text-green-700'
          >
            ×
          </button>
        </div>
      )}

      {/* Header */}
      <div className='flex justify-between items-center'>
        <div>
          <h1 className='text-2xl font-bold text-gray-900'>Link Management</h1>
          <p className='text-gray-600'>Create, manage, and track your links</p>
        </div>
        <Button onClick={handleCreateAd} className='btn-enhanced hover-lift'>
          <Plus className='w-4 h-4 mr-2' />
          Create Link
        </Button>
      </div>

      {/* Stats Overview */}
      {ads.length > 0 && (
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-6'>
          <Card className='card-enhanced'>
            <CardContent className='p-4'>
              <div className='flex items-center'>
                <div className='p-2 bg-blue-100 rounded-lg'>
                  <Eye className='w-4 h-4 text-blue-600' />
                </div>
                <div className='ml-3'>
                  <p className='text-sm font-medium text-gray-600'>
                    Total Links
                  </p>
                  <p className='text-2xl font-bold text-gray-900'>
                    {ads.length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className='card-enhanced'>
            <CardContent className='p-4'>
              <div className='flex items-center'>
                <div className='p-2 bg-orange-100 rounded-lg'>
                  <TrendingUp className='w-4 h-4 text-orange-600' />
                </div>
                <div className='ml-3'>
                  <p className='text-sm font-medium text-gray-600'>
                    User Tracking Enabled
                  </p>
                  <p className='text-2xl font-bold text-gray-900'>
                    {ads.filter(ad => ad.userTrackingEnabled).length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className='card-enhanced'>
            <CardContent className='p-4'>
              <div className='flex items-center'>
                <div className='p-2 bg-purple-100 rounded-lg'>
                  <TrendingUp className='w-4 h-4 text-purple-600' />
                </div>
                <div className='ml-3'>
                  <p className='text-sm font-medium text-gray-600'>
                    Total Clicks
                  </p>
                  <p className='text-2xl font-bold text-gray-900'>
                    {AdsService.formatNumber(
                      ads.reduce((sum, ad) => sum + (ad.clickCount || 0), 0)
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Ads List */}
      {ads.length === 0 ? (
        <Card className='card-enhanced'>
          <CardContent className='p-8 text-center'>
            <div className='mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4'>
              <Plus className='w-8 h-8 text-gray-400' />
            </div>
            <h3 className='text-lg font-medium text-gray-900 mb-2'>
              No links yet
            </h3>
            <p className='text-gray-600 mb-4'>
              Create your first link to start tracking clicks and analytics.
            </p>
            <Button onClick={handleCreateAd} className='btn-enhanced'>
              Create Your First Link
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className='grid gap-4'>
          {ads.map(ad => (
            <Card key={ad.id} className='card-enhanced hover-lift gap-1'>
              <CardHeader className='pb-3'>
                <div className='flex items-start justify-between'>
                  <div className='flex-1'>
                    <CardTitle className='text-lg font-semibold text-gray-900'>
                      {ad.name}
                    </CardTitle>
                    <div className='flex items-center space-x-2 mt-2'>
                      {/* <Badge variant={ad.active ? 'default' : 'secondary'}>
                        {ad.active ? 'Active' : 'Inactive'}
                      </Badge> */}
                      <Badge
                        variant={
                          ad.userTrackingEnabled ? 'default' : 'destructive'
                        }
                      >
                        {ad.userTrackingEnabled
                          ? 'User Tracking On'
                          : 'User Tracking Off'}
                      </Badge>
                    </div>
                  </div>
                  <div className='flex items-center space-x-2'>
                    {/* <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => handleViewAnalytics(ad)}
                      className='hover-lift'
                    >
                      <TrendingUp className='w-4 h-4' />
                    </Button> */}
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => handleEditAd(ad)}
                      className='hover-lift'
                    >
                      <Edit2 className='w-4 h-4' />
                    </Button>
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => handleDeleteAd(ad)}
                      className='text-red-600 hover:text-red-800 hover-lift'
                    >
                      <Trash2 className='w-4 h-4' />
                    </Button>
                  </div>
                </div>
              </CardHeader>

              <CardContent>
                {ad.description && (
                  <p className='text-gray-600 mb-4'>{ad.description}</p>
                )}

                <div className='space-y-3'>
                  <div className='flex items-center justify-between'>
                    <span className='text-sm font-medium text-gray-600'>
                      Destination URL:
                    </span>
                    <div className='flex items-center space-x-2'>
                      <span className='text-sm text-gray-900 truncate max-w-xs'>
                        {ad.destinationUrl}
                      </span>
                      <Button
                        variant='ghost'
                        size='sm'
                        onClick={() => window.open(ad.destinationUrl, '_blank')}
                        className='hover-lift'
                      >
                        <ExternalLink className='w-3 h-3' />
                      </Button>
                    </div>
                  </div>

                  <div className='flex items-center justify-between'>
                    <span className='text-sm font-medium text-gray-600'>
                      Tracking URL:
                    </span>
                    <div className='flex items-center space-x-2'>
                      <code className='text-xs bg-gray-100 px-2 py-1 rounded truncate max-w-xs'>
                        {ad.trackingUrl}
                      </code>
                      <Button
                        variant='ghost'
                        size='sm'
                        onClick={() => handleCopyTrackingUrl(ad)}
                        className='hover-lift'
                      >
                        <Copy className='w-3 h-3' />
                      </Button>
                    </div>
                  </div>

                  <div className='flex items-center justify-between pt-2 border-t border-gray-200'>
                    <div className='flex items-center space-x-4'>
                      <div className='text-center'>
                        <p className='text-lg font-bold text-gray-900'>
                          {AdsService.formatNumber(ad.clickCount || 0)}
                        </p>
                        <p className='text-xs text-gray-600'>Clicks</p>
                      </div>
                      <div className='text-center'>
                        <p className='text-lg font-bold text-gray-900'>
                          {AdsService.formatNumber(ad.uniqueUsersCount || 0)}
                        </p>
                        <p className='text-xs text-gray-600'>Unique Users</p>
                      </div>
                    </div>

                    {/* <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => handleToggleTracking(ad)}
                      className='hover-lift'
                    >
                      {ad.userTrackingEnabled ? (
                        <ToggleRight className='w-4 h-4 text-green-600' />
                      ) : (
                        <ToggleLeft className='w-4 h-4 text-gray-400' />
                      )}
                    </Button> */}
                  </div>
                </div>

                <div className='mt-3 text-xs text-gray-500'>
                  Created by {ad.creator.firstName} {ad.creator.lastName} on{' '}
                  {AdsService.formatDate(ad.createdAt)}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modals */}
      <AdFormModal
        isOpen={isFormModalOpen}
        onClose={() => setIsFormModalOpen(false)}
        onSuccess={handleFormSuccess}
        ad={selectedAd}
        isEdit={isEditMode}
      />

      <AdAnalyticsModal
        isOpen={isAnalyticsModalOpen}
        onClose={() => setIsAnalyticsModalOpen(false)}
        ad={selectedAd}
      />
    </div>
  );
};
